import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { translatePolicyKey } from "../PolicyDetails/PolicyDetails.tsx";
import { Translations } from "../../enum/translations.enum.ts";
import FooterSummary from "./FooterSummary/FooterSummary.tsx";

const SEPARATOR = ",";

export interface CustomColumn {
  name: string;
  header: string;
  type?: "string" | "number";
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  render?: (cellProps: any) => React.ReactNode;
}

interface CustomDataTableProps {
  columns: CustomColumn[];
  dataSource: string[] | object[];
  isLoading: boolean;
  gridTitle: string;
  idProperty: string;
  editable?: boolean;
  onEditComplete?: (e: any) => void;
  onAdd?: () => void;
  onRowClick?: (row: any) => void;
  onDelete?: () => void;
  shouldShowAddAndDeleteButtons?: boolean;
  csvFilename?: string;
  icon?: string;
  onExportCSV?: () => void;
  printReportMetadata?: Array<{ label: string; value: string }>;
  printReportSummary?: string | null;
}

interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

interface ColumnFilter {
  [columnName: string]: Set<string>;
}

const DailyReportDataTable: React.FC<CustomDataTableProps> = ({
  columns,
  dataSource,
  isLoading,
  gridTitle,
  idProperty,
  onAdd,
  onDelete,
  onRowClick = () => {},
  shouldShowAddAndDeleteButtons = false,
  csvFilename = "grid-data.csv",
  onExportCSV,
  printReportMetadata,
  printReportSummary,
}) => {
  const [filterText, setFilterText] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>({});
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [filterPosition, setFilterPosition] = useState({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
        setOpenFilterColumn(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const columnUniqueValues = useMemo(() => {
    const values: Record<string, string[]> = {};

    columns.forEach((col) => {
      const crossFilteredData = dataSource.filter((item: any) => {
        const matchesGlobalSearch =
          filterText === "" ||
          Object.values(item).some((value) =>
            String(value).toLowerCase().includes(filterText.toLowerCase())
          );

        if (!matchesGlobalSearch) return false;

        for (const [columnName, selectedValues] of Object.entries(
          columnFilters
        )) {
          if (columnName === col.name || selectedValues.size === 0) continue;

          const itemValue = String(item[columnName] ?? "").trim();
          if (!selectedValues.has(itemValue)) {
            return false;
          }
        }
        return true;
      });

      values[col.name] = Array.from(
        new Set(
          crossFilteredData.map((item: any) => {
            const val = item[col.name] ?? "";
            return String(val).trim();
          })
        )
      ).sort();
    });

    return values;
  }, [dataSource, columns, filterText, columnFilters]);

  const filteredDataSource = useMemo(() => {
    return dataSource.filter((item: any) => {
      const matchesGlobalSearch =
        filterText === "" ||
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(filterText.toLowerCase())
        );

      if (!matchesGlobalSearch) return false;

      for (const [columnName, selectedValues] of Object.entries(
        columnFilters
      )) {
        if (selectedValues.size === 0) continue;

        const itemValue = String(item[columnName] ?? "").trim();
        if (!selectedValues.has(itemValue)) {
          return false;
        }
      }

      return true;
    });
  }, [dataSource, filterText, columnFilters]);

  const sortedData = useMemo(() => {
    const sorted = [...filteredDataSource];

    if (sortConfig) {
      sorted.sort((a: any, b: any) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        const aStr = String(aVal ?? "").toLowerCase();
        const bStr = String(bVal ?? "").toLowerCase();

        const aNum = parseFloat(aStr);
        const bNum = parseFloat(bStr);

        const isNumeric = !isNaN(aNum) && !isNaN(bNum);
        const compare = isNumeric ? aNum - bNum : aStr.localeCompare(bStr);

        return sortConfig.direction === "asc" ? compare : -compare;
      });
    }

    return sorted;
  }, [filteredDataSource, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      data: isFullscreen ? sortedData : sortedData.slice(startIndex, endIndex),
      total: sortedData.length,
    };
  }, [sortedData, currentPage, pageSize, isFullscreen]);

  const columnSums = useMemo(() => {
    const sums: Record<string, number> = {};

    columns.forEach((col) => {
      if (col.type === "number") {
        sums[col.name] = sortedData.reduce((acc, row: any) => {
          const val = parseFloat(String(row[col.name] ?? 0));
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
      }
    });

    return sums;
  }, [sortedData, columns]);

  const totalPages = Math.ceil(paginatedData.total / pageSize);

  const handleSort = (columnName: string) => {
    setSortConfig((prev) => {
      if (prev?.key === columnName) {
        return prev.direction === "asc"
          ? { key: columnName, direction: "desc" }
          : null;
      }
      return { key: columnName, direction: "asc" };
    });
    setCurrentPage(1);
  };

  const handleColumnFilterToggle = (columnName: string, value: string) => {
    setSortConfig(null);
    setColumnFilters((prev) => {
      const newFilters = { ...prev };

      if (!newFilters[columnName]) {
        newFilters[columnName] = new Set();
      }

      const newSet = new Set(newFilters[columnName]);

      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }

      if (newSet.size === 0) {
        delete newFilters[columnName];
      } else {
        newFilters[columnName] = newSet;
      }

      return newFilters;
    });

    setCurrentPage(1);
  };

  const handleResizeMouseDown = (e: React.MouseEvent, colName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const thElement = (e.target as HTMLElement).parentElement;
    if (!thElement) return;

    const startWidth = thElement.getBoundingClientRect().width;
    const startX = e.clientX;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      setColumnWidths((prev) => ({
        ...prev,
        [colName]: Math.max(60, startWidth + deltaX),
      }));
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const downloadBlob = (blob: Blob, fileName = "grid-data.csv") => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.position = "absolute";
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportCSV = () => {
    const lines: string[] = [];
    // IMPORTANT: Always export ALL filtered data, regardless of current page.
    // If user has applied filters/search, only those matching rows are included.
    const activeDataRows = sortedData;

    if (printReportMetadata?.length) {
      lines.push("ПАРАМЕТРИ НА СПРАВКАТА" + SEPARATOR);
      printReportMetadata.forEach((meta) => {
        lines.push(`${meta.label}${SEPARATOR}${meta.value}`);
      });
      lines.push("");
    }

    // Add footer summary at the top
    const summary = {
      totalPremium: 0,
      gfAmount: 0,
      stickerAmount: 0,
      agentCommission: 0,
      officeCommission: 0,
      taxAmount: 0,
    };

    activeDataRows.forEach((row: any) => {
      summary.totalPremium += parseFloat(row.totalSum || 0);
      summary.gfAmount += parseFloat(row.insuranceFundSum || 0);
      summary.stickerAmount += parseFloat(row.stickerSum || 0);
      summary.agentCommission += parseFloat(row.commissionAgentAmount || 0);
      summary.officeCommission += parseFloat(row.commissionOfficeAmount || 0);
      summary.taxAmount += parseFloat(row.twoPercentSum || 0);
    });

    const formatCurrency = (value: number) =>
      value.toLocaleString("bg-BG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    lines.push("ОБОБЩЕНИЕ" + SEPARATOR);
    lines.push(
      `Обща сума${SEPARATOR}${formatCurrency(summary.totalPremium).replace(
        ",",
        "."
      )}`
    );
    lines.push(
      `Сума за ГФ${SEPARATOR}${formatCurrency(summary.gfAmount).replace(
        ",",
        "."
      )}`
    );
    lines.push(
      `Сума Стикери${SEPARATOR}${formatCurrency(summary.stickerAmount).replace(
        ",",
        "."
      )}`
    );
    lines.push(
      `Комисион за агенти${SEPARATOR}${formatCurrency(
        summary.agentCommission
      ).replace(",", ".")}`
    );
    lines.push(
      `Комисион за офиса${SEPARATOR}${formatCurrency(
        summary.officeCommission
      ).replace(",", ".")}`
    );
    lines.push(
      `Сума за данък 2%${SEPARATOR}${formatCurrency(summary.taxAmount).replace(
        ",",
        "."
      )}`
    );
    lines.push(".");

    if (printReportMetadata) {
      lines.push("ДАННИ" + SEPARATOR);
    }

    const header = columns
      .map((c) => translatePolicyKey(c.name))
      .join(SEPARATOR);

    lines.push(header);

    activeDataRows.forEach((row: any) => {
      const lineRow = columns
        .map((col) => {
          const val = row[col.name] ?? "";
          const str = String(val);

          return str.includes(SEPARATOR) || str.includes("\n")
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(SEPARATOR);

      lines.push(lineRow);
    });

    // Append the full raw summary underneath the data extract
    if (printReportSummary) {
      lines.push("");
      lines.push("ОБОБЩЕНИЕ (ПЪЛНО)" + SEPARATOR);

      const escape = (str: string) =>
        str.includes(SEPARATOR) || str.includes('"')
          ? `"${str.replace(/"/g, '""')}"`
          : str;

      printReportSummary
        .split("\n")
        .filter((line) => line.trim() !== "")
        .forEach((line) => {
          const parts = line.split(" : ");

          if (parts.length >= 2) {
            const title = parts[0].trim();
            const rest = parts.slice(1).join(" : ").trim();

            const match = rest.match(/^(.*?)\s*(\(.*\))?$/);
            const value = match?.[1]?.trim() ?? rest;
            const note = match?.[2]?.trim() ?? "";

            lines.push(
              `${escape(title)}${SEPARATOR}${escape(value)}${SEPARATOR}${escape(
                note
              )}`
            );
          } else {
            lines.push(escape(line));
          }
        });
    }

    const blob = new Blob(["\uFEFF" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    downloadBlob(blob, csvFilename);
  };

  const enterPrintMode = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const currentDate = new Date().toLocaleString("bg-BG");

    const activePrintRows = sortedData;

    const showingStart = activePrintRows.length > 0 ? 1 : 0;
    const showingEnd = activePrintRows.length;

    let printMetadataHTML = "";
    if (printReportMetadata && printReportMetadata.length > 0) {
      printMetadataHTML = `
      <div class="print-meta-grid">
        ${printReportMetadata
          .map(
            (m) => `
          <div class="print-meta-cell">
            <span class="meta-label">${m.label}:</span>
            <span class="meta-value">${m.value || "—"}</span>
          </div>
        `
          )
          .join("")}
      </div>
    `;
    }

    const summary = {
      totalPremium: 0,
      gfAmount: 0,
      stickerAmount: 0,
      agentCommission: 0,
      officeCommission: 0,
      taxAmount: 0,
    };

    activePrintRows.forEach((row: any) => {
      summary.totalPremium += parseFloat(row.totalSum || 0);
      summary.gfAmount += parseFloat(row.insuranceFundSum || 0);
      summary.stickerAmount += parseFloat(row.stickerSum || 0);
      summary.agentCommission += parseFloat(row.commissionAgentAmount || 0);
      summary.officeCommission += parseFloat(row.commissionOfficeAmount || 0);
      summary.taxAmount += parseFloat(row.twoPercentSum || 0);
    });

    const formatCurrency = (value: number) =>
      value.toLocaleString("bg-BG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const printSummaryHTML = `
    <div class="summary-wrapper">
      <div class="summary-card">
        <div class="summary-title">Обща сума</div>
        <div class="summary-value">${formatCurrency(summary.totalPremium)}</div>
        <div class="summary-subtitle">(вкл. т.2,т.3,т.4 и т.9)</div>
      </div>

      <div class="summary-card">
        <div class="summary-title">Сума за ГФ</div>
        <div class="summary-value">${formatCurrency(summary.gfAmount)}</div>
      </div>

      <div class="summary-card">
        <div class="summary-title">Сума Стикери</div>
        <div class="summary-value">${formatCurrency(
          summary.stickerAmount
        )}</div>
      </div>

      <div class="summary-card">
        <div class="summary-title">Комисион за агенти</div>
        <div class="summary-value">${formatCurrency(
          summary.agentCommission
        )}</div>
        <div class="summary-subtitle">(общо)</div>
      </div>

      <div class="summary-card">
        <div class="summary-title">Комисион за офиса</div>
        <div class="summary-value">${formatCurrency(
          summary.officeCommission
        )}</div>
        <div class="summary-subtitle">(вкл. Банка/ЗК)</div>
      </div>

      <div class="summary-card">
        <div class="summary-title">Сума за данък 2%</div>
        <div class="summary-value">${formatCurrency(summary.taxAmount)}</div>
      </div>
    </div>
  `;

    const colgroup = `<colgroup>
        ${columns
          .map((col) => {
            const width = columnWidths[col.name] || col.minWidth;
            return `<col style="width: ${width ? width + "px" : "auto"}">`;
          })
          .join("")}
      </colgroup>`;

    const tableHeaders = columns
      .map(
        (col) =>
          `<th class="${col.type === "number" ? "number-cell" : ""}">
        ${col.header}
      </th>`
      )
      .join("");

    const tableRows = activePrintRows
      .map(
        (row: any) => `
        <tr>
          ${columns
            .map(
              (col) =>
                `<td class="${col.type === "number" ? "number-cell" : ""}">
                  ${row[col.name] ?? ""}
                </td>`
            )
            .join("")}
        </tr>
      `
      )
      .join("");

    const html = `
    <html>
      <head>
        <title>${gridTitle}</title>

        <style>
          @page {
            size: landscape;
            margin: 5mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            font-family: Arial, sans-serif;
            color: #111827;
            background: white;
            margin: 0;
            padding: 6px;
            zoom: 0.82;
          }

          .report-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #8a1d06;
            padding-bottom: 6px;
            margin-bottom: 8px;
          }

          .report-title {
            font-size: 16px;
            font-weight: bold;
            color: #1c2434;
          }

          .report-meta-block {
            text-align: right;
            font-size: 10px;
            color: #64748b;
            line-height: 1.4;
          }

          .report-filter-badge {
            font-size: 10px;
            color: #8a1d06;
            font-weight: 500;
            margin-top: 2px;
          }

          .print-meta-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            padding: 6px;
            margin-bottom: 10px;
            font-size: 10px;
          }

          .print-meta-cell {
            display: flex;
            flex-direction: column;
          }

          .meta-label {
            color: #64748b;
            font-weight: 500;
          }

          .meta-value {
            color: #1c2434;
            font-weight: 600;
          }

          .summary-wrapper {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 8px;
            margin-bottom: 10px;
          }

          .summary-card {
            border: 1px solid #dbe4ee;
            background: #f8fafc;
            padding: 8px;
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-height: 72px;
          }

          .summary-title {
            font-size: 9px;
            font-weight: 600;
            color: #64748b;
          }

          .summary-value {
            font-size: 14px;
            font-weight: 700;
            color: #1c2434;
            line-height: 1.2;
          }

          .summary-subtitle {
            font-size: 8px;
            color: #94a3b8;
          }

          table {
            width: auto;    
            min-width: 100%; 
            border-collapse: collapse;
            table-layout: fixed;
            font-size: 9px;
            line-height: 1.2;
            margin-bottom: 10px;
          }

          thead {
            background: #f1f5f9;
          }

          th {
            border: 1px solid #cbd5e1;
            padding: 4px 6px;
            text-align: left;
            font-weight: 600;
            color: #1c2434;
          }

          td {
            border: 1px solid #e2e8f0;
            padding: 4px 6px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            color: #64748b;
          }

          th:first-child,
          td:first-child {
            width: 130px;
            min-width: 130px;
            overflow: visible;
            white-space: nowrap;
            text-overflow: clip;
          }

          tr:nth-child(even) {
            background: #f8fafc;
          }

          .number-cell {
            text-align: right;
            font-variant-numeric: tabular-nums;
          }
          
           .raw-summary {
            border: 1px solid #e2e8f0;
            background: #f8fafc;
            margin-bottom: 10px;
          }

          .raw-summary-title {
            font-size: 10px;
            font-weight: 600;
            color: #1c2434;
            padding: 5px 8px;
            border-bottom: 1px solid #e2e8f0;
          }

          .raw-summary-body {
            font-size: 9px;
            color: #374151;
            padding: 6px 8px;
            margin: 0;
            white-space: pre-wrap;
            font-family: monospace;
          }

          .report-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 10px;
            border-top: 1px solid #e2e8f0;
            padding-top: 6px;
            font-size: 10px;
            color: #64748b;
          }

          .report-footer-bold {
            font-weight: 600;
            color: #1c2434;
          }

          @media print {
            body {
              margin: 0;
              padding: 0;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>

      <body>
        <div class="report-header">
          <div>
            <div class="report-title">${gridTitle}</div>

            ${
              filterText
                ? `
                <div class="report-filter-badge">
                  Приложен филтър: "${filterText}"
                </div>
              `
                : ""
            }
          </div>

          <div class="report-meta-block">
            <div>Файл: ${csvFilename}</div>
            <div>Генерирано на: ${currentDate}</div>
          </div>
        </div>

        ${printMetadataHTML}

        ${printSummaryHTML}

        <table>
          ${colgroup}
          <thead>
            <tr>${tableHeaders}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
         ${
           printReportSummary
             ? `
        <div class="raw-summary">
          <div class="raw-summary-title">📊 Обобщение</div>
          <pre class="raw-summary-body">${printReportSummary}</pre>
        </div>
        `
             : ""
         }


        <div class="report-footer">
          <div>
            ${Translations.SHOWING_TEXT}
            <span class="report-footer-bold">
              ${showingStart} - ${showingEnd}
            </span>
            ${Translations.OF_TEXT}
            <span class="report-footer-bold">
              ${activePrintRows.length}
            </span>
          </div>

          <div>
            Общо записи в системата:
            <span class="report-footer-bold">
              ${dataSource.length}
            </span>
          </div>
        </div>

        <script>
          window.onload = () => {
            window.print();
            window.close();
          };
        </script>
      </body>
    </html>
  `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const renderToolbarButtons = () => (
    <div className="flex items-center gap-1.5">
      {shouldShowAddAndDeleteButtons && (
        <>
          <button
            className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
            onClick={onAdd}
          >
            <i className="fa-solid fa-plus text-[10px]" />
          </button>

          <button
            className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
            onClick={onDelete}
          >
            <i className="fa-solid fa-minus text-[10px]" />
          </button>
        </>
      )}

      <button
        className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
        onClick={() => {
          setIsSearchVisible(!isSearchVisible);
          if (isSearchVisible) {
            setFilterText("");
            setSortConfig(null);
          }
        }}
      >
        <i className="fa-solid fa-search text-[10px]" />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          isSearchVisible ? "ml-1 w-40 opacity-100" : "w-0 opacity-0"
        }`}
      >
        <input
          type="text"
          placeholder="Филтър..."
          value={filterText}
          onChange={(e) => {
            setSortConfig(null);
            setFilterText(e.target.value);
            setCurrentPage(1);
          }}
          className="border-stroke focus:border-primary focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark w-full rounded-sm border bg-white px-2 py-1 text-[11px] text-black outline-none transition-all focus:ring-2 dark:text-white"
        />
      </div>

      <button
        className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
        onClick={onExportCSV ?? handleExportCSV}
      >
        <i className="fa-solid fa-download text-[10px]" />
      </button>

      <button
        className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
        onClick={() => setIsFullscreen(true)}
      >
        <i className="fa-solid fa-expand text-[10px]" />
      </button>

      <button
        className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
        onClick={enterPrintMode}
      >
        <i className="fa-solid fa-print text-[10px]" />
      </button>
    </div>
  );

  const renderColumnFilterDropdown = (columnName: string) => {
    const isOpen = openFilterColumn === columnName;
    const values = columnUniqueValues[columnName] || [];
    const selectedValues = columnFilters[columnName] || new Set();

    return (
      <div
        key={`filter-${columnName}`}
        className="z-10 flex flex-shrink-0 items-center"
      >
        <button
          className={`h-5 w-5 items-center justify-center rounded-sm text-[9px] transition-all duration-150
          ${
            selectedValues.size > 0
              ? "bg-primary flex text-white opacity-100 shadow-md"
              : "hidden bg-transparent text-slate-400 hover:bg-slate-200 hover:text-slate-700 group-hover:flex dark:hover:bg-slate-700 dark:hover:text-white"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (isOpen) {
              setOpenFilterColumn(null);
              return;
            }

            const rect = e.currentTarget.getBoundingClientRect();
            const columnIndex = columns.findIndex((c) => c.name === columnName);
            const shouldOpenRight = columnIndex < 2;

            setFilterPosition({
              top: rect.bottom + 6,
              left: shouldOpenRight ? rect.left : rect.right - 240,
            });

            setOpenFilterColumn(columnName);
          }}
        >
          <i className="fa-solid fa-filter" />
        </button>

        {isOpen &&
          createPortal(
            <>
              <div
                className="fixed inset-0 z-[999998]"
                onClick={() => setOpenFilterColumn(null)}
              />

              <div
                style={{
                  top: filterPosition.top,
                  left: filterPosition.left,
                }}
                className="
                  animate-in fade-in zoom-in-95
                  fixed z-[999999]
                  max-h-[min(28rem,calc(100vh-120px))]
                  w-60
                  overflow-hidden
                  rounded-none
                  border
                  border-slate-200
                  bg-white/95
                  shadow-[0_10px_40px_rgba(0,0,0,0.18)]
                  backdrop-blur-md
                  transition-all
                  duration-200
                  dark:border-slate-700
                  dark:bg-slate-900/95
                "
              >
                <div className="sticky top-0 z-10 border-b border-slate-200 bg-slate-700/95 px-3 py-2 backdrop-blur-md dark:border-slate-700 dark:bg-slate-800/95">
                  <div className="truncate text-[11px] font-semibold tracking-wide text-white">
                    {translatePolicyKey(columnName)}
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto py-1">
                  {values.length === 0 ? (
                    <div className="px-3 py-4 text-center text-[11px] text-slate-500 dark:text-slate-400">
                      Няма налични опции
                    </div>
                  ) : (
                    values.map((value) => (
                      <label
                        key={`${columnName}-${value}`}
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[11px] transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        <input
                          type="checkbox"
                          checked={selectedValues.has(value)}
                          onChange={() =>
                            handleColumnFilterToggle(columnName, value)
                          }
                          className="accent-primary h-3.5 w-3.5 cursor-pointer rounded-none"
                        />

                        <span className="flex-1 truncate font-normal text-slate-700 dark:text-slate-200">
                          {value || (
                            <span className="italic text-slate-400">
                              (empty)
                            </span>
                          )}
                        </span>
                      </label>
                    ))
                  )}
                </div>

                {selectedValues.size > 0 && (
                  <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                    <button
                      onClick={() => {
                        setSortConfig(null);
                        setColumnFilters((prev) => {
                          const newFilters = { ...prev };
                          delete newFilters[columnName];
                          return newFilters;
                        });
                      }}
                      className="text-primary text-[10px] transition-opacity hover:opacity-70"
                    >
                      Изчисти всички
                    </button>
                  </div>
                )}
              </div>
            </>,
            document.body
          )}
      </div>
    );
  };

  const tableContent = (
    <div className="relative z-0 overflow-visible">
      <div className="relative z-0 overflow-auto rounded-none">
        <table className="w-full table-fixed border-collapse text-[13px] leading-tight text-black">
          <thead>
            <tr className="dark:bg-meta-4 sticky top-0 z-20 bg-gray-100/95 shadow-[0_1px_0_rgba(0,0,0,0.04)] backdrop-blur-md">
              {columns.map((col) => {
                const currentWidth = columnWidths[col.name] || col.minWidth;

                return (
                  <th
                    key={col.name}
                    style={{
                      width: currentWidth ? `${currentWidth}px` : undefined,
                    }}
                    className="border-stroke dark:border-strokedark group relative whitespace-nowrap border bg-gray-50 px-2 py-2 text-left text-[11px] font-semibold leading-none text-slate-700 transition-colors hover:bg-slate-100 dark:text-white dark:hover:bg-slate-800"
                  >
                    <div className="flex h-5 w-full items-center justify-between gap-1">
                      <div
                        onClick={() => handleSort(col.name)}
                        className={`flex min-w-0 flex-1 cursor-pointer select-none items-center gap-1 overflow-hidden ${
                          col.type === "number"
                            ? "flex-row-reverse justify-start text-right"
                            : "justify-start text-left"
                        }`}
                      >
                        <span className="truncate">{col.header}</span>

                        {sortConfig?.key === col.name && (
                          <span className="text-primary flex-shrink-0 text-[11px]">
                            {sortConfig.direction === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </div>

                      {renderColumnFilterDropdown(col.name)}
                    </div>

                    <div
                      onMouseDown={(e) => handleResizeMouseDown(e, col.name)}
                      className="hover:after:bg-primary absolute bottom-0 right-0 top-0 w-2 cursor-col-resize bg-transparent transition-all after:absolute after:right-0 after:top-1/2 after:h-5 after:w-[2px] after:-translate-y-1/2 after:bg-transparent after:transition-colors"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border-stroke dark:border-strokedark border px-2 py-6 text-center text-[11px] text-slate-500 dark:text-slate-300"
                >
                  {Translations.LOADING}
                </td>
              </tr>
            ) : paginatedData.data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border-stroke dark:border-strokedark border px-2 py-6 text-center text-[11px] text-slate-500 dark:text-slate-300"
                >
                  {Translations.NO_DATA}
                </td>
              </tr>
            ) : (
              paginatedData.data.map((row: any, rowIndex) => (
                <tr
                  key={`${row[idProperty]}-${rowIndex}`}
                  className={`
                      border-stroke dark:border-strokedark
                      dark:hover:bg-meta-4/20
                      dark:bg-boxdark
                      border-b
                      transition-all
                      duration-150
                      hover:bg-slate-100/80
                      hover:shadow-[inset_0_0_0_9999px_rgba(15,23,42,0.015)]
                      ${
                        rowIndex % 2 === 0
                          ? "dark:bg-boxdark bg-white"
                          : "bg-slate-50/60 dark:bg-slate-900/20"
                      }
                    `}
                  onClick={() => onRowClick(row)}
                >
                  {columns.map((col) => (
                    <td
                      key={`${col.name}-${rowIndex}`}
                      title={String(row[col.name] ?? "")}
                      className={`border-stroke dark:border-strokedark border px-2 py-[6px] align-middle text-[11px] leading-tight text-slate-700 dark:text-slate-200 ${
                        col.type === "number" ? "text-right font-mono" : ""
                      }`}
                    >
                      {col.render ? (
                        col.render({
                          value: row[col.name],
                          row,
                        })
                      ) : (
                        <span className="block truncate">
                          {row[col.name] ?? ""}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const fullscreenModal = createPortal(
    <>
      <div
        className="fixed inset-0 z-[99998] bg-black/60 backdrop-blur-[3px]"
        onClick={() => setIsFullscreen(false)}
      />

      <div className="dark:bg-boxdark border-stroke dark:border-strokedark fixed inset-2 z-[99999] flex flex-col overflow-hidden rounded-none border bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
        <div className="border-stroke dark:border-strokedark flex min-h-[44px] items-center justify-between border-b px-3 py-2">
          <h3 className="text-[12px] font-semibold tracking-wide text-black dark:text-white">
            {gridTitle}
          </h3>

          <button
            className="bg-primary hover:bg-primary/90 flex h-7 w-7 items-center justify-center rounded-sm text-white shadow-sm transition-all duration-150 hover:scale-[1.03]"
            onClick={() => setIsFullscreen(false)}
          >
            <i className="fa-solid fa-xmark text-[10px]" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-1">{tableContent}</div>
      </div>
    </>,
    document.body
  );

  return (
    <>
      <div className="w-full p-4">
        <div className="border-stroke dark:border-strokedark dark:bg-boxdark flex flex-col overflow-hidden rounded-none border bg-white shadow-sm transition-all duration-200">
          <div className="border-stroke dark:border-strokedark flex items-center justify-between gap-2 border-b px-3 py-2">
            <h3 className="text-[12px] font-semibold tracking-wide text-black dark:text-white">
              {gridTitle}
            </h3>

            {renderToolbarButtons()}
          </div>

          {tableContent}

          {!isFullscreen && Object.keys(columnSums).length > 0 && (
            <div className="border-stroke border-t bg-slate-50 px-3 py-3 text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200">
              <div className="flex w-full items-start gap-3">
                <FooterSummary filteredData={filteredDataSource} />
              </div>
            </div>
          )}

          {!isFullscreen && (
            <div className="border-stroke dark:border-strokedark dark:bg-meta-4/10 relative z-[1] flex items-center justify-between gap-2 border-t bg-gray-50 px-3 py-2 text-[11px] font-medium text-slate-700 dark:text-slate-200">
              <div>
                {Translations.SHOWING_TEXT}{" "}
                {Math.min(
                  (currentPage - 1) * pageSize + 1,
                  paginatedData.total
                )}{" "}
                - {Math.min(currentPage * pageSize, paginatedData.total)}{" "}
                {Translations.OF_TEXT} {paginatedData.total}
              </div>

              <div className="ml-auto flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border-stroke dark:border-strokedark dark:bg-boxdark focus:ring-primary/20 rounded-sm border bg-white px-2 py-1 text-[11px] font-medium text-slate-700 outline-none transition-all focus:ring-2 dark:text-white"
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={1000}>1000</option>
                </select>

                <div className="flex items-center gap-1">
                  <button
                    className="border-stroke dark:border-strokedark hover:bg-primary dark:bg-boxdark flex h-6 w-6 items-center justify-center rounded-sm border bg-white text-[10px] font-semibold text-slate-700 transition-all duration-150 hover:text-white disabled:opacity-40 dark:text-white"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    ◀
                  </button>

                  <span className="min-w-[50px] text-center text-[11px] font-semibold text-black dark:text-white">
                    {currentPage} / {totalPages || 1}
                  </span>

                  <button
                    className="border-stroke dark:border-strokedark hover:bg-primary dark:bg-boxdark flex h-6 w-6 items-center justify-center rounded-sm border bg-white text-[10px] font-semibold text-slate-700 transition-all duration-150 hover:text-white disabled:opacity-40 dark:text-white"
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    ▶
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isFullscreen && fullscreenModal}
    </>
  );
};

export default DailyReportDataTable;
